import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/it';
import { theme } from './theme/theme';
import { AuthProvider, useAuth } from './context/AuthContext';
import { BillingProvider } from './context/BillingContext';
import { PlatformAuthProvider } from './context/PlatformAuthContext';
import { SnackbarProvider } from './context/SnackbarContext';
import { NotificationProvider } from './context/NotificationContext';
import { OutboxProvider } from './outbox/outboxStore';
import { ProtectedRoute } from './routes/ProtectedRoute';
import { GuestRoute } from './routes/GuestRoute';
import { AdminRoute } from './routes/AdminRoute';
import { PlatformProtectedRoute } from './routes/PlatformProtectedRoute';
import { PlatformGuestRoute } from './routes/PlatformGuestRoute';
import { LoadingScreen } from './components/LoadingScreen';
import { WriteBlockedMessageBridge } from './components/WriteBlockedMessageBridge';

import { LoginPage } from './pages/LoginPage';
import { AcceptInvitePage } from './pages/AcceptInvitePage';
import { AppShellPage } from './pages/AppShellPage';
import { ChangePasswordPage } from './pages/ChangePasswordPage';
import { PlatformLoginPage } from './pages/platform/PlatformLoginPage';
import { PlatformLayout } from './pages/platform/PlatformLayout';
import { DeepLinkNavigator } from './components/DeepLinkNavigator';
import { PublicLayout } from './layouts/PublicLayout';
import { LandingPage } from './pages/public/LandingPage';
import { FeaturesPage } from './pages/public/FeaturesPage';
import { PricingPage } from './pages/public/PricingPage';
import { ContactPage } from './pages/public/ContactPage';
import { PrivacyPage } from './pages/public/PrivacyPage';
import { TermsPage } from './pages/public/TermsPage';
import { SignaturePage } from './pages/public/SignaturePage';

const DashboardPage = lazy(() =>
  import('./pages/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const NotificationsPage = lazy(() =>
  import('./pages/NotificationsPage').then((m) => ({ default: m.NotificationsPage })),
);
const WorksitesListPage = lazy(() =>
  import('./pages/worksites/WorksitesListPage').then((m) => ({ default: m.WorksitesListPage })),
);
const WorksiteDetailPage = lazy(() =>
  import('./pages/worksites/WorksiteDetailPage').then((m) => ({ default: m.WorksiteDetailPage })),
);
const WorksiteFormPage = lazy(() =>
  import('./pages/worksites/WorksiteFormPage').then((m) => ({ default: m.WorksiteFormPage })),
);
const UsersPage = lazy(() =>
  import('./pages/users/UsersPage').then((m) => ({ default: m.UsersPage })),
);
const UserCostsPage = lazy(() =>
  import('./pages/finance/UserCostsPage').then((m) => ({ default: m.UserCostsPage })),
);
const FinanceAlertsPage = lazy(() =>
  import('./pages/finance/FinanceAlertsPage').then((m) => ({ default: m.FinanceAlertsPage })),
);
const FinanceDashboardPage = lazy(() =>
  import('./pages/finance/FinanceDashboardPage').then((m) => ({ default: m.FinanceDashboardPage })),
);
const AnalyticsCorePage = lazy(() =>
  import('./pages/analytics/AnalyticsCorePage').then((m) => ({ default: m.AnalyticsCorePage })),
);
const ProcurementAnalyticsPage = lazy(() =>
  import('./pages/analytics/ProcurementAnalyticsPage').then((m) => ({ default: m.ProcurementAnalyticsPage })),
);
const CustomersListPage = lazy(() =>
  import('./pages/customers/CustomersListPage').then((m) => ({ default: m.CustomersListPage })),
);
const CustomerDetailPage = lazy(() =>
  import('./pages/customers/CustomerDetailPage').then((m) => ({ default: m.CustomerDetailPage })),
);
const CustomerFormPage = lazy(() =>
  import('./pages/customers/CustomerFormPage').then((m) => ({ default: m.CustomerFormPage })),
);
const TenantsListPage = lazy(() =>
  import('./pages/platform/TenantsListPage').then((m) => ({ default: m.TenantsListPage })),
);
const TenantDetailPage = lazy(() =>
  import('./pages/platform/TenantDetailPage').then((m) => ({ default: m.TenantDetailPage })),
);
const GlobalReportsListPage = lazy(() =>
  import('./pages/reports/GlobalReportsListPage').then((m) => ({ default: m.GlobalReportsListPage })),
);
const ReportDetailPage = lazy(() =>
  import('./pages/reports/ReportDetailPage').then((m) => ({ default: m.ReportDetailPage })),
);
const ReportFormPage = lazy(() =>
  import('./pages/reports/ReportFormPage').then((m) => ({ default: m.ReportFormPage })),
);
const DocumentFormPage = lazy(() =>
  import('./pages/documents/DocumentFormPage').then((m) => ({ default: m.DocumentFormPage })),
);
const DocumentDetailPage = lazy(() =>
  import('./pages/documents/DocumentDetailPage').then((m) => ({ default: m.DocumentDetailPage })),
);
const GlobalDocumentsListPage = lazy(() =>
  import('./pages/documents/GlobalDocumentsListPage').then((m) => ({ default: m.GlobalDocumentsListPage })),
);
const OutboxPage = lazy(() =>
  import('./pages/outbox/OutboxPage').then((m) => ({ default: m.OutboxPage })),
);
const NotificationSettingsPage = lazy(() =>
  import('./pages/settings/NotificationSettingsPage').then((m) => ({
    default: m.NotificationSettingsPage,
  })),
);
const IntegrationsPage = lazy(() =>
  import('./pages/integrations/IntegrationsPage').then((m) => ({ default: m.IntegrationsPage })),
);
const ApiAccessPage = lazy(() =>
  import('./pages/integrations/ApiAccessPage').then((m) => ({ default: m.ApiAccessPage })),
);
const ExternalApiAuditLogPage = lazy(() =>
  import('./pages/integrations/ExternalApiAuditLogPage').then((m) => ({ default: m.ExternalApiAuditLogPage })),
);
const DeveloperDocsPage = lazy(() =>
  import('./pages/integrations/DeveloperDocsPage').then((m) => ({ default: m.DeveloperDocsPage })),
);
const ProfilePage = lazy(() =>
  import('./pages/ProfilePage').then((m) => ({ default: m.ProfilePage })),
);
const RemindersLayout = lazy(() =>
  import('./pages/reminders/RemindersLayout').then((m) => ({ default: m.RemindersLayout })),
);
const RemindersTemplatesPage = lazy(() =>
  import('./pages/reminders/RemindersTemplatesPage').then((m) => ({ default: m.RemindersTemplatesPage })),
);
const RemindersSettingsPage = lazy(() =>
  import('./pages/reminders/RemindersSettingsPage').then((m) => ({ default: m.RemindersSettingsPage })),
);
const RemindersLogsPage = lazy(() =>
  import('./pages/reminders/RemindersLogsPage').then((m) => ({ default: m.RemindersLogsPage })),
);
const AuditPage = lazy(() =>
  import('./pages/audit/AuditPage').then((m) => ({ default: m.AuditPage })),
);
const AttendanceDailyPage = lazy(() =>
  import('./pages/attendance/AttendanceDailyPage').then((m) => ({ default: m.AttendanceDailyPage })),
);
const WorksiteReportPage = lazy(() =>
  import('./pages/attendance/WorksiteReportPage').then((m) => ({ default: m.WorksiteReportPage })),
);
const AttendanceWorksiteSelectPage = lazy(() =>
  import('./pages/attendance/AttendanceWorksiteSelectPage').then((m) => ({
    default: m.AttendanceWorksiteSelectPage,
  })),
);
const EquipmentListPage = lazy(() =>
  import('./pages/equipment/EquipmentListPage').then((m) => ({ default: m.EquipmentListPage })),
);
const EquipmentDetailPage = lazy(() =>
  import('./pages/equipment/EquipmentDetailPage').then((m) => ({ default: m.EquipmentDetailPage })),
);
const MaterialsListPage = lazy(() =>
  import('./pages/materials/MaterialsListPage').then((m) => ({ default: m.MaterialsListPage })),
);
const MaterialDetailPage = lazy(() =>
  import('./pages/materials/MaterialDetailPage').then((m) => ({ default: m.MaterialDetailPage })),
);
const MaterialFormPage = lazy(() =>
  import('./pages/materials/MaterialFormPage').then((m) => ({ default: m.MaterialFormPage })),
);
const MaterialsDraftReviewPage = lazy(() =>
  import('./pages/materials/MaterialsDraftReviewPage').then((m) => ({ default: m.MaterialsDraftReviewPage })),
);
const WarehouseConfigPage = lazy(() =>
  import('./pages/config/WarehouseConfigPage').then((m) => ({ default: m.WarehouseConfigPage })),
);
const DeliveryNoteDetailPage = lazy(() =>
  import('./pages/delivery-notes/DeliveryNoteDetailPage').then((m) => ({ default: m.DeliveryNoteDetailPage })),
);
const DeliveryNoteFormPage = lazy(() =>
  import('./pages/delivery-notes/DeliveryNoteFormPage').then((m) => ({ default: m.DeliveryNoteFormPage })),
);
const SupplierMaterialMappingsPage = lazy(() =>
  import('./pages/delivery-notes/SupplierMaterialMappingsPage').then((m) => ({ default: m.SupplierMaterialMappingsPage })),
);
const SupplierListPage = lazy(() =>
  import('./pages/suppliers/SupplierListPage').then((m) => ({ default: m.SupplierListPage })),
);
const SupplierDetailPage = lazy(() =>
  import('./pages/suppliers/SupplierDetailPage').then((m) => ({ default: m.SupplierDetailPage })),
);
const PlanningPage = lazy(() =>
  import('./pages/planning/PlanningPage').then((m) => ({ default: m.PlanningPage })),
);
const ChecklistTemplatesPage = lazy(() =>
  import('./pages/checklists/ChecklistTemplatesPage').then((m) => ({ default: m.ChecklistTemplatesPage })),
);
const ChecklistInstanceDetailPage = lazy(() =>
  import('./pages/checklists/ChecklistInstanceDetailPage').then((m) => ({ default: m.ChecklistInstanceDetailPage })),
);

function AppHomeRedirect() {
  return (
    <ProtectedRoute>
      <RoleBasedHomeRedirect />
    </ProtectedRoute>
  );
}

function RoleBasedHomeRedirect() {
  const { user } = useAuth();
  const target = user?.role === 'OPERATORE' ? 'worksites' : 'dashboard';
  return <Navigate to={target} replace />;
}

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
      <SnackbarProvider>
        <WriteBlockedMessageBridge />
        <AuthProvider>
          <BillingProvider>
          <OutboxProvider>
          <NotificationProvider>
          <PlatformAuthProvider>
            <BrowserRouter>
              {/* DeepLinkNavigator: consumes pending push deep-link once auth resolves (R8.2) */}
              <DeepLinkNavigator />
              <Suspense fallback={<LoadingScreen />}>
                <Routes>
                  {/* R20.6: Firma cliente — pagina pubblica senza auth */}
                  <Route path="/sign/:token" element={<SignaturePage />} />

                  {/* R14.4 Public area — no auth */}
                  <Route path="/" element={<PublicLayout />}>
                    <Route index element={<LandingPage />} />
                    <Route path="features" element={<FeaturesPage />} />
                    <Route path="pricing" element={<PricingPage />} />
                    <Route path="contact" element={<ContactPage />} />
                    <Route path="privacy" element={<PrivacyPage />} />
                    <Route path="terms" element={<TermsPage />} />
                  </Route>

                  {/* Platform (Super Admin) — separate auth */}
                  <Route
                    path="/platform/login"
                    element={
                      <PlatformGuestRoute>
                        <PlatformLoginPage />
                      </PlatformGuestRoute>
                    }
                  />
                  <Route
                    path="/platform"
                    element={
                      <PlatformProtectedRoute>
                        <PlatformLayout />
                      </PlatformProtectedRoute>
                    }
                  >
                    <Route index element={<Navigate to="tenants" replace />} />
                    <Route path="tenants" element={<TenantsListPage />} />
                    <Route path="tenants/:tenantId" element={<TenantDetailPage />} />
                  </Route>

                  {/* Guest routes (product) */}
                  <Route
                    path="/login"
                  element={
                    <GuestRoute>
                      <LoginPage />
                    </GuestRoute>
                  }
                />
                <Route path="/accept-invite" element={<AcceptInvitePage />} />

                {/* Protected area — AppShell acts as layout (Outlet) */}
                <Route
                  path="/app"
                  element={
                    <ProtectedRoute>
                      <AppShellPage />
                    </ProtectedRoute>
                  }
                >
                  {/* Default landing varies by role */}
                  <Route index element={<AppHomeRedirect />} />

                  {/* R0.4: Change password (forced when mustChangePassword) */}
                  <Route path="change-password" element={<ChangePasswordPage />} />

                  {/* Profilo utente (nome, cognome, data di nascita) */}
                  <Route path="profile" element={<ProfilePage />} />

                  {/* Dashboard */}
                  <Route
                    path="dashboard"
                    element={
                      <ProtectedRoute allowedRoles={['AMMINISTRATORE_AZIENDA', 'RESPONSABILE']}>
                        <DashboardPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Planning (R16) — Admin, Resp, Operatore (read-only for Operatore) */}
                  <Route path="planning" element={<PlanningPage />} />

                  {/* Checklist (R17) — feature CHECKLIST; Admin, Resp, Operatore (menu + tab commessa) */}
                  <Route path="checklists/templates" element={<ChecklistTemplatesPage />} />
                  <Route path="checklists/instances/:id" element={<ChecklistInstanceDetailPage />} />

                  {/* Notifications */}
                  <Route path="notifications" element={<NotificationsPage />} />

                  {/* Worksites — lazy */}
                  <Route path="worksites" element={<WorksitesListPage />} />
                  <Route path="worksites/new" element={<WorksiteFormPage />} />
                  <Route path="worksites/:id" element={<WorksiteDetailPage />} />
                  <Route path="worksites/:id/edit" element={<WorksiteFormPage />} />
                  <Route path="worksites/:worksiteId/delivery-notes/new" element={<DeliveryNoteFormPage />} />

                  {/* Customers (CRM) — admin/responsabile only; OPERATORE redirected to /app */}
                  <Route
                    path="customers"
                    element={
                      <ProtectedRoute allowedRoles={['AMMINISTRATORE_AZIENDA', 'RESPONSABILE']}>
                        <CustomersListPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="customers/new"
                    element={
                      <ProtectedRoute allowedRoles={['AMMINISTRATORE_AZIENDA', 'RESPONSABILE']}>
                        <CustomerFormPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="customers/:id"
                    element={
                      <ProtectedRoute allowedRoles={['AMMINISTRATORE_AZIENDA', 'RESPONSABILE']}>
                        <CustomerDetailPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="customers/:id/edit"
                    element={
                      <ProtectedRoute allowedRoles={['AMMINISTRATORE_AZIENDA', 'RESPONSABILE']}>
                        <CustomerFormPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* R21.5 Fornitori — Admin/Resp only; DELIVERY_NOTES feature */}
                  <Route
                    path="suppliers"
                    element={
                      <ProtectedRoute allowedRoles={['AMMINISTRATORE_AZIENDA', 'RESPONSABILE']}>
                        <SupplierListPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="suppliers/:id"
                    element={
                      <ProtectedRoute allowedRoles={['AMMINISTRATORE_AZIENDA', 'RESPONSABILE']}>
                        <SupplierDetailPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Users — admin only, lazy */}
                  <Route
                    path="users"
                    element={
                      <AdminRoute>
                        <UsersPage />
                      </AdminRoute>
                    }
                  />

                  {/* Finance (R9.1, R9.3, R9.4) — Dashboard for all; User costs & Alerts admin/resp only */}
                  <Route path="finance/dashboard" element={<FinanceDashboardPage />} />
                  <Route
                    path="finance/user-costs"
                    element={
                      <ProtectedRoute allowedRoles={['AMMINISTRATORE_AZIENDA', 'RESPONSABILE']}>
                        <UserCostsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="finance/alerts"
                    element={
                      <ProtectedRoute allowedRoles={['AMMINISTRATORE_AZIENDA', 'RESPONSABILE']}>
                        <FinanceAlertsPage />
                      </ProtectedRoute>
                    }
                  />
                  {/* R18.1 Analytics core — Admin/Resp only; FINANCE_ALERTS required */}
                  <Route
                    path="analytics"
                    element={
                      <ProtectedRoute allowedRoles={['AMMINISTRATORE_AZIENDA', 'RESPONSABILE']}>
                        <AnalyticsCorePage />
                      </ProtectedRoute>
                    }
                  />
                  {/* R18.2 Procurement analytics — Admin/Resp only; DELIVERY_NOTES required */}
                  <Route
                    path="analytics/procurement"
                    element={
                      <ProtectedRoute allowedRoles={['AMMINISTRATORE_AZIENDA', 'RESPONSABILE']}>
                        <ProcurementAnalyticsPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* R10.1 Reminders — ADMIN/RESP only */}
                  <Route
                    path="reminders"
                    element={
                      <ProtectedRoute allowedRoles={['AMMINISTRATORE_AZIENDA', 'RESPONSABILE']}>
                        <RemindersLayout />
                      </ProtectedRoute>
                    }
                  >
                    <Route index element={<Navigate to="templates" replace />} />
                    <Route path="templates" element={<RemindersTemplatesPage />} />
                    <Route path="settings" element={<RemindersSettingsPage />} />
                    <Route path="logs" element={<RemindersLogsPage />} />
                  </Route>

                  {/* R13.1-FE Equipment — ADMIN/RESP only */}
                  <Route
                    path="equipment"
                    element={
                      <ProtectedRoute allowedRoles={['AMMINISTRATORE_AZIENDA', 'RESPONSABILE']}>
                        <EquipmentListPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="equipment/:id"
                    element={
                      <ProtectedRoute allowedRoles={['AMMINISTRATORE_AZIENDA', 'RESPONSABILE']}>
                        <EquipmentDetailPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* R14 Materials — all roles (OPERATORE read-only, no new/edit in UI). R14.3.4: drafts review admin/resp. */}
                  <Route path="materials" element={<MaterialsListPage />} />
                  <Route
                    path="materials/drafts"
                    element={
                      <ProtectedRoute allowedRoles={['AMMINISTRATORE_AZIENDA', 'RESPONSABILE']}>
                        <MaterialsDraftReviewPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="materials/new"
                    element={
                      <ProtectedRoute allowedRoles={['AMMINISTRATORE_AZIENDA', 'RESPONSABILE']}>
                        <MaterialFormPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="materials/:id" element={<MaterialDetailPage />} />
                  <Route
                    path="materials/:id/edit"
                    element={
                      <ProtectedRoute allowedRoles={['AMMINISTRATORE_AZIENDA', 'RESPONSABILE']}>
                        <MaterialFormPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="config/warehouse"
                    element={
                      <ProtectedRoute allowedRoles={['AMMINISTRATORE_AZIENDA', 'RESPONSABILE']}>
                        <WarehouseConfigPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* R11.1-FE Audit log — ADMIN/RESP only */}
                  <Route
                    path="audit"
                    element={
                      <ProtectedRoute allowedRoles={['AMMINISTRATORE_AZIENDA', 'RESPONSABILE']}>
                        <AuditPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* R12.1-FE Attendance — all roles: daily; Admin/Resp: worksite report + Ore per commessa */}
                  <Route path="attendance" element={<AttendanceDailyPage />} />
                  <Route
                    path="attendance/worksite"
                    element={
                      <ProtectedRoute allowedRoles={['AMMINISTRATORE_AZIENDA', 'RESPONSABILE']}>
                        <AttendanceWorksiteSelectPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="attendance/worksite/:id"
                    element={
                      <ProtectedRoute allowedRoles={['AMMINISTRATORE_AZIENDA', 'RESPONSABILE']}>
                        <WorksiteReportPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Reports (R5.2) */}
                  <Route path="reports" element={<GlobalReportsListPage />} />
                  <Route path="reports/new" element={<ReportFormPage />} />
                  <Route path="reports/:reportId" element={<ReportDetailPage />} />
                  <Route path="reports/:reportId/edit" element={<ReportFormPage />} />
                  <Route path="reports/:reportId/delivery-notes/new" element={<DeliveryNoteFormPage />} />
                  <Route path="delivery-notes/:id" element={<DeliveryNoteDetailPage />} />
                  <Route path="delivery-notes/:id/edit" element={<DeliveryNoteFormPage />} />
                  <Route path="supplier-material-mappings" element={<SupplierMaterialMappingsPage />} />

                  {/* Documents (R6.2) — admin/responsabile only; OPERATORE redirected to /app */}
                  <Route
                    path="documents"
                    element={
                      <ProtectedRoute allowedRoles={['AMMINISTRATORE_AZIENDA', 'RESPONSABILE']}>
                        <GlobalDocumentsListPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="documents/new"
                    element={
                      <ProtectedRoute allowedRoles={['AMMINISTRATORE_AZIENDA', 'RESPONSABILE']}>
                        <DocumentFormPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="documents/:documentId"
                    element={
                      <ProtectedRoute allowedRoles={['AMMINISTRATORE_AZIENDA', 'RESPONSABILE']}>
                        <DocumentDetailPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="documents/:documentId/edit"
                    element={
                      <ProtectedRoute allowedRoles={['AMMINISTRATORE_AZIENDA', 'RESPONSABILE']}>
                        <DocumentFormPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Outbox (R7.2) */}
                  <Route path="outbox" element={<OutboxPage />} />

                  {/* Settings (R8.2) */}
                  <Route path="settings/notifications" element={<NotificationSettingsPage />} />

                  {/* R20.1 / R20.2 Integrazioni */}
                  <Route
                    path="integrations"
                    element={
                      <ProtectedRoute allowedRoles={['AMMINISTRATORE_AZIENDA', 'RESPONSABILE']}>
                        <IntegrationsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="integrations/api-access"
                    element={
                      <ProtectedRoute allowedRoles={['AMMINISTRATORE_AZIENDA', 'RESPONSABILE']}>
                        <ApiAccessPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="integrations/api-audit"
                    element={
                      <ProtectedRoute allowedRoles={['AMMINISTRATORE_AZIENDA', 'RESPONSABILE']}>
                        <ExternalApiAuditLogPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="integrations/docs"
                    element={
                      <ProtectedRoute allowedRoles={['AMMINISTRATORE_AZIENDA', 'RESPONSABILE']}>
                        <DeveloperDocsPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Catch-all inside /app */}
                  <Route path="*" element={<Navigate to="worksites" replace />} />
                </Route>

                  {/* Global catch-all: unknown paths → public home */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </PlatformAuthProvider>
          </NotificationProvider>
          </OutboxProvider>
          </BillingProvider>
        </AuthProvider>
      </SnackbarProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
}
